import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoadingBlock } from "@/components/Spinner/LoadingBlock";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { InputField } from "@/components/InputField/InputField";
import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";
import { useSnackBar } from "@/utils/useSnackbar";

import {
	useGetPipedriveDealConfigQuery,
	useSavePipedriveDealConfigMutation,
	useGetPipedrivePipelinesQuery,
	useGetPipedriveOwnersQuery,
	useGetPipedriveSourceFieldsQuery,
} from "@/pages/Private/redux/pipedrive/pipedrive.api";

import { PipedriveDealConfig } from "../schema/pipedrive";

const sanitizeStr = (val: string | null | undefined): string | null => {
	if (!val || val === "undefined" || val === "null") {
		return null;
	}

	return val;
};

const DEFAULT_CURRENCIES: { id: string; title: string }[] = [
	{ id: "USD", title: "USD - US Dollar" },
	{ id: "EUR", title: "EUR - Euro" },
	{ id: "GBP", title: "GBP - British Pound" },
	{ id: "CHF", title: "CHF - Swiss Franc" },
	{ id: "CAD", title: "CAD - Canadian Dollar" },
	{ id: "AUD", title: "AUD - Australian Dollar" },
];

export const PipedriveDealConfigTab: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.pipedrive.dealConfig.${key}`);

	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const { data: dealConfig, isLoading: isLoadingConfig } = useGetPipedriveDealConfigQuery();
	const { data: pipelinesData } = useGetPipedrivePipelinesQuery();
	const { data: ownersData } = useGetPipedriveOwnersQuery();
	const { data: sourceFields } = useGetPipedriveSourceFieldsQuery();
	const [saveDealConfig, { isLoading: isSaving }] = useSavePipedriveDealConfigMutation();

	const [config, setConfig] = useState<PipedriveDealConfig>({
		defaultPipelineId: null,
		defaultStageId: null,
		dealNameTemplate: "",
		expectedCloseDateOffset: 30,
		defaultOwnerId: null,
		defaultCurrency: null,
	});

	useEffect(() => {
		if (dealConfig) {
			setConfig({
				defaultPipelineId: sanitizeStr(dealConfig.defaultPipelineId),
				defaultStageId: sanitizeStr(dealConfig.defaultStageId),
				dealNameTemplate:
					dealConfig.dealNameTemplate && dealConfig.dealNameTemplate !== "undefined"
						? dealConfig.dealNameTemplate
						: "",
				expectedCloseDateOffset: dealConfig.expectedCloseDateOffset ?? 30,
				defaultOwnerId: sanitizeStr(dealConfig.defaultOwnerId),
				defaultCurrency: sanitizeStr(dealConfig.defaultCurrency),
			});
		}
	}, [dealConfig]);

	const pipelineOptions: DropdownAutoCompleteItem[] = useMemo(
		() => pipelinesData?.pipelines?.map((p) => ({ id: p.id, title: p.label })) || [],
		[pipelinesData]
	);

	const stageOptions: DropdownAutoCompleteItem[] = useMemo(() => {
		if (!config.defaultPipelineId) {
			return [];
		}

		const selectedPipeline = pipelinesData?.pipelines?.find(
			(p) => p.id === config.defaultPipelineId
		);

		return selectedPipeline?.stages?.map((s) => ({ id: s.id, title: s.label })) || [];
	}, [pipelinesData, config.defaultPipelineId]);

	const ownerOptions: DropdownAutoCompleteItem[] = useMemo(
		() =>
			ownersData?.owners?.map((o) => ({
				id: o.id,
				title: o.name ? `${o.name} (${o.email})` : o.email,
			})) || [],
		[ownersData]
	);

	const placeholderOptions: DropdownAutoCompleteItem[] = useMemo(
		() =>
			(sourceFields?.dealFields ?? sourceFields?.personFields ?? []).map((f) => ({
				id: f.name,
				title: `{${f.name}} - ${f.label}`,
			})),
		[sourceFields]
	);

	const handleInsertPlaceholder = (value: DropdownAutoCompleteItem) => {
		setConfig((prev) => ({
			...prev,
			dealNameTemplate: `${prev.dealNameTemplate ?? ""}{${value.id}}`,
		}));
	};

	const handleSave = async () => {
		try {
			const payload: PipedriveDealConfig = {
				defaultPipelineId: sanitizeStr(config.defaultPipelineId),
				defaultStageId: sanitizeStr(config.defaultStageId),
				dealNameTemplate:
					config.dealNameTemplate && config.dealNameTemplate !== "undefined"
						? config.dealNameTemplate
						: null,
				expectedCloseDateOffset: config.expectedCloseDateOffset ?? null,
				defaultOwnerId: sanitizeStr(config.defaultOwnerId),
				defaultCurrency: sanitizeStr(config.defaultCurrency),
			};

			await saveDealConfig(payload).unwrap();
			handleOpenSnackbar(ts("saved"));
		} catch {
			handleOpenSnackbar(ts("saveError"));
		}
	};

	if (isLoadingConfig) {
		return <LoadingBlock />;
	}

	return (
		<div className="flex flex-col w-full gap-4">
			{renderMessageSnackbar()}
			<div className="text-sm text-text-meta mb-1">{ts("description")}</div>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<Dropdown
					data={pipelineOptions}
					defaultValue={
						config.defaultPipelineId
							? pipelineOptions.find((p) => p.id === config.defaultPipelineId)
							: undefined
					}
					floating={true}
					handleSelect={(value) => {
						setConfig((prev) => ({
							...prev,
							defaultPipelineId: value.id as string,
							defaultStageId: null,
						}));
					}}
					label={ts("defaultPipeline")}
				/>

				<Dropdown
					key={config.defaultPipelineId ?? "no-pipeline"}
					data={stageOptions}
					defaultValue={
						config.defaultStageId
							? stageOptions.find((s) => s.id === config.defaultStageId)
							: undefined
					}
					disabled={!config.defaultPipelineId}
					floating={true}
					handleSelect={(value) => {
						setConfig((prev) => ({ ...prev, defaultStageId: value.id as string }));
					}}
					label={ts("defaultStage")}
				/>

				<div className="md:col-span-2 flex flex-col gap-2">
					<InputField
						handleChange={(e) =>
							setConfig((prev) => ({ ...prev, dealNameTemplate: e.target.value }))
						}
						information={ts("dealNameTemplateHelp")}
						label={ts("dealNameTemplate")}
						name="dealNameTemplate"
						placeholder="{company} - {firstName} {lastName}"
						value={config.dealNameTemplate || ""}
					/>
					{placeholderOptions.length > 0 && (
						<div className="max-w-[400px]">
							<Dropdown
								data={placeholderOptions}
								floating={true}
								handleSelect={handleInsertPlaceholder}
								label={ts("insertPlaceholder")}
							/>
						</div>
					)}
				</div>

				<InputField
					handleChange={(e) =>
						setConfig((prev) => ({
							...prev,
							expectedCloseDateOffset: parseInt(e.target.value, 10) || 0,
						}))
					}
					information={ts("closeDateOffsetHelp")}
					label={ts("closeDateOffsetDays")}
					min={1}
					name="expectedCloseDateOffset"
					type="number"
					value={config.expectedCloseDateOffset ?? 30}
				/>

				<Dropdown
					data={ownerOptions}
					defaultValue={
						config.defaultOwnerId
							? ownerOptions.find((o) => String(o.id) === String(config.defaultOwnerId))
							: undefined
					}
					floating={true}
					handleSelect={(value) => {
						setConfig((prev) => ({
							...prev,
							defaultOwnerId: value.id as string,
						}));
					}}
					label={ts("defaultOwner")}
				/>

				<Dropdown
					data={DEFAULT_CURRENCIES}
					defaultValue={
						config.defaultCurrency
							? DEFAULT_CURRENCIES.find((c) => c.id === config.defaultCurrency)
							: undefined
					}
					floating={true}
					handleSelect={(value) => {
						setConfig((prev) => ({ ...prev, defaultCurrency: value.id as string }));
					}}
					label={ts("defaultCurrency")}
				/>
			</div>

			<div className="flex justify-end mt-4">
				<Button
					color={ButtonColor.ACTION}
					isLoading={isSaving}
					size={ButtonSize.S}
					title={t("basics.save")}
					type="button"
					onClick={handleSave}
				/>
			</div>
		</div>
	);
};
